import { useState } from 'react';
import { shareApi } from '@app/api/organization';
import type { PlanItem, ServicePlan } from '@app/types';
import type { ShareLimitData } from './types';

export function useSetlistShare(items: PlanItem[], plan: ServicePlan) {
  const [isSharing, setIsSharing] = useState(false);
  const [shareTooltip, setShareTooltip] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [limitData, setLimitData] = useState<ShareLimitData | null>(null);
  const [limitDeleting, setLimitDeleting] = useState<string | null>(null);

  const buildPlaylist = () => {
    const songs = items
      .filter(item => item.type === 'Song' && item.youtubeUrl)
      .map(item => {
        const url = item.youtubeUrl || '';
        const match = url.match(/[?&]v=([^&]+)/) || url.match(/youtu\.be\/([^?&]+)/);
        const videoId = match?.[1] || '';
        return {
          yt: videoId,
          t: item.title,
          k: item.key || undefined,
          n: item.notes || undefined,
        };
      })
      .filter(s => s.yt);
    return {
      title: plan.title || 'Setlist',
      date: plan.date,
      songs,
    };
  };

  const copyShareUrl = async (id: string) => {
    const memberAppUrl = import.meta.env.VITE_MEMBER_APP_URL || 'http://localhost:3001';
    const shareUrl = `${memberAppUrl}/p/${id}`;
    await navigator.clipboard.writeText(shareUrl);
    setShareTooltip(true);
    setTimeout(() => setShareTooltip(false), 2000);
  };

  const handleShare = async () => {
    if (items.length === 0 || isSharing) return;
    setIsSharing(true);
    setShareError(null);
    try {
      const playlist = buildPlaylist();
      if (playlist.songs.length === 0) {
        setShareError('Add YouTube links to songs before sharing');
        setTimeout(() => setShareError(null), 3000);
        return;
      }
      const result = await shareApi.create(playlist);
      await copyShareUrl(result.id);
    } catch (err: any) {
      if (err?.response?.status === 409 && err?.response?.data?.existingLinks) {
        setLimitData({
          maxSlots: err.response.data.maxSlots || 5,
          existingLinks: err.response.data.existingLinks,
        });
      } else if (err?.response?.status === 429) {
        setShareError('Daily share limit reached. Try again tomorrow.');
        setTimeout(() => setShareError(null), 4000);
      } else {
        setShareError('Failed to create share link');
        setTimeout(() => setShareError(null), 3000);
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleLimitReplace = async (replaceId: string) => {
    setIsSharing(true);
    setLimitData(null);
    try {
      const playlist = buildPlaylist();
      const result = await shareApi.createWithReplace(playlist, replaceId);
      await copyShareUrl(result.id);
    } catch {
      setShareError('Failed to replace share link');
      setTimeout(() => setShareError(null), 3000);
    } finally {
      setIsSharing(false);
    }
  };

  const handleLimitDelete = async (shareId: string) => {
    setLimitDeleting(shareId);
    try {
      await shareApi.delete(shareId);
      setLimitData(null);
      setLimitDeleting(null);
      // Retry share now that a slot is free
      handleShare();
    } catch {
      setShareError('Failed to delete setlist');
      setTimeout(() => setShareError(null), 3000);
      setLimitDeleting(null);
    }
  };

  return {
    isSharing,
    shareTooltip,
    shareError,
    limitData,
    setLimitData,
    limitDeleting,
    handleShare,
    handleLimitReplace,
    handleLimitDelete,
  };
}
